'use client';

import { useEffect, useState } from 'react';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import type { AiSearchProgress } from '@/lib/ai-search-progress';

export function AiSearchLoading({ progress }: { progress: AiSearchProgress }) {
  const { t } = useTranslation();
  const [showSecondStep, setShowSecondStep] = useState(false);

  useEffect(() => {
    const remaining = 600 - (Date.now() - progress.startedAt);
    const timer = window.setTimeout(() => setShowSecondStep(true), Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [progress.startedAt]);

  const activeStep = progress.phase === 'loading' ? 2 : showSecondStep ? 1 : 0;
  const steps = ['understandingRequest', 'buildingFilters', 'loadingResultsStep'] as const;

  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-busy="true">
      <div role="status" aria-live="polite" className="m-3 rounded-lg border border-primary/30 bg-primary/5 p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 size-5 shrink-0 text-primary motion-safe:animate-pulse" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-foreground">{t('search.ai.progressQuery', { query: progress.query })}</p>
            <ol className="mt-3 space-y-2 text-sm">
              {steps.map((step, index) => (
                <li key={step} className={`flex items-center gap-2 ${index > activeStep ? 'text-muted-foreground' : 'text-foreground'}`}>
                  <span className="flex size-5 shrink-0 items-center justify-center" aria-hidden>
                    {index < activeStep ? <Check className="size-4 text-primary" /> : index === activeStep
                      ? <Loader2 className="size-4 text-primary motion-safe:animate-spin" />
                      : <span className="size-2 rounded-full bg-muted-foreground/40" />}
                  </span>
                  <span className={index === activeStep ? 'font-medium text-primary' : ''}>{t(`search.ai.${step}`)}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      <div aria-hidden className="space-y-3 px-3 pb-4 pt-2">
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className="flex items-center gap-4 rounded-md border p-4">
            <Skeleton className="size-9 shrink-0 animate-none motion-safe:animate-pulse" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-3/5 animate-none motion-safe:animate-pulse" />
              <Skeleton className="h-3 w-2/5 animate-none motion-safe:animate-pulse" />
            </div>
            <Skeleton className="hidden h-4 w-24 animate-none motion-safe:animate-pulse lg:block" />
          </div>
        ))}
      </div>
    </div>
  );
}
